import { Box, SelectChangeEvent, useTheme } from "@mui/material";
import { ProductVariantSelector } from "./ProductVariantSelector";
import { QuantitySelector } from "./QuantitySelector";
import { PriceDisplay } from "./PriceDisplay";
import { AddToCartButton } from "./AddToCartButton";

interface ProductVariant {
  product_id: string;
  variant_specifics: Array<{ value: string }>;
}

interface ProductPurchaseCardProps {
  variants?: ProductVariant[];
  selectedVariantId?: string;
  onVariantChange: (event: SelectChangeEvent<string>) => void;
  quantity: number;
  onQuantityChange: (event: SelectChangeEvent<number>) => void;
  amount: number;
  onAddToCart: () => void;
  disabled: boolean;
  submitting: boolean;
  isLoading: boolean;
  variant?: "mobile" | "desktop";
}

export const ProductPurchaseCard = ({
  variants,
  selectedVariantId,
  onVariantChange,
  quantity,
  onQuantityChange,
  amount,
  onAddToCart,
  disabled,
  submitting,
  isLoading,
  variant = "mobile",
}: ProductPurchaseCardProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        marginTop: variant === "mobile" ? { xs: 2, sm: 2.5 } : 0,
        marginBottom: variant === "desktop" ? 3 : 0,
        padding:
          variant === "mobile" ? { xs: 2, sm: 2.5 } : { xs: 2, sm: 2.5, md: 3 },
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        borderRadius: 3,
        border: "1px solid #e8e8e8",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
          borderRadius: "12px 12px 0 0",
        },
      }}
    >
      {/* Product Variants */}
      <ProductVariantSelector
        variants={variants}
        selectedVariantId={selectedVariantId}
        onVariantChange={onVariantChange}
        isLoading={isLoading}
      />

      {/* Quantity and Price Row */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <QuantitySelector
          quantity={quantity}
          onQuantityChange={onQuantityChange}
          variant={variant}
        />

        {!disabled && (
          <PriceDisplay amount={amount} quantity={quantity} variant={variant} />
        )}
      </Box>

      {/* Add to Cart Button */}
      <AddToCartButton
        onClick={onAddToCart}
        disabled={disabled}
        submitting={submitting}
        variant={variant}
      />
    </Box>
  );
};
